import React from 'react';
import styles from './Home.module.css';
import * as utils from '../../utils/utils';
import HQLogo from '../../assets/HQLogo.png';

const STREAM_ROTATE_MS = 3000;
const VISIBLE_STREAM_COUNT = 5;
const STREAM_REFILL_THRESHOLD = 4;

const FALLBACK_STREAM = [
	{ personName: 'Olivia Carter', type: 'skill', item: 'Product Analytics' },
	{ personName: 'Ethan Walker', type: 'tool', item: 'Figma' },
	{ personName: 'Madison Brooks', type: 'soft-skill', item: 'Cross-team Communication' },
	{ personName: 'Logan Mitchell', type: 'domain', item: 'Growth Marketing' },
	{ personName: 'Avery Johnson', type: 'tool', item: 'Notion' },
	{ personName: 'Chloe Bennett', type: 'skill', item: 'Full-stack Web Development' },
	{ personName: 'Mason Cooper', type: 'domain', item: 'Fintech Operations' },
];

export default class Home extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			isFetchingMore: false,
			errorText: '',

			streamOffset: 0,
			streamPerTypeLimit: 5,
			visibleStreamItems: [],
			pendingStreamItems: [],
			latestInsertedStreamId: null,
			visibleSections: {
				hero: true,
			},
		};

		this.rotateInterval = null;
		this.streamIdCounter = 0;
		this.sectionObserver = null;
		this.sectionRefs = {};
	}

	componentDidMount() {
		this.loadPublicStream();
		this.rotateInterval = setInterval(this.rotateStream, STREAM_ROTATE_MS);
		this.setupSectionObserver();
	}

	componentWillUnmount() {
		if (this.rotateInterval) {
			clearInterval(this.rotateInterval);
		}

		if (this.sectionObserver) {
			this.sectionObserver.disconnect();
		}
	}

	setSectionRef = (sectionKey) => (sectionEl) => {
		this.sectionRefs[sectionKey] = sectionEl;

		if (this.sectionObserver && sectionEl) {
			this.sectionObserver.observe(sectionEl);
		}
	};

	setupSectionObserver = () => {
		if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
			return;
		}

		this.sectionObserver = new window.IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const sectionKey = entry.target.getAttribute('data-section-key');

					if (sectionKey) {
						this.setState((prevState) => ({
							visibleSections: {
								...prevState.visibleSections,
								[sectionKey]: true,
							},
						}));
						this.sectionObserver.unobserve(entry.target);
					}
				}
			});
		}, {
			threshold: 0.22,
			rootMargin: '0px 0px -8% 0px',
		});

		Object.keys(this.sectionRefs).forEach((sectionKey) => {
			const sectionEl = this.sectionRefs[sectionKey];
			if (sectionEl) {
				this.sectionObserver.observe(sectionEl);
			}
		});
	};

	getRevealClass = (sectionKey) => {
		const {
			visibleSections,
		} = this.state;

		if (visibleSections[sectionKey]) {
			return styles.revealVisible;
		}

		return styles.revealHidden;
	};

	isValidPublicStreamItem = (item) => {
		if (!item || typeof item !== 'object') {
			return false;
		}

		if (typeof item.personName !== 'string' || item.personName.trim() === '') {
			return false;
		}

		if (typeof item.item !== 'string' || item.item.trim() === '') {
			return false;
		}

		return ['skill', 'tool', 'soft-skill', 'domain'].includes(item.type);
	};

	decorateStreamItems = (items) => {
		return items.map((item) => ({
			...item,
			_streamId: `stream-${this.streamIdCounter++}`,
		}));
	};

	loadPublicStream = () => {
		const {
			isFetchingMore,
			streamOffset,
		} = this.state;

		if (isFetchingMore) {
			return;
		}

		this.setState({
			isFetchingMore: true,
		});

		utils.sendRequest('/stream/getPublicStream', {
			offset: streamOffset,
		})
			.then((responseData) => {
				const incoming = this.decorateStreamItems((responseData.publicStreamItems || []).filter(this.isValidPublicStreamItem));
				const perTypeLimit = responseData.perTypeLimit || 5;

				this.setState((prevState) => {
					let nextPending = [...prevState.pendingStreamItems, ...incoming];
					let nextVisible = prevState.visibleStreamItems;

					if (nextVisible.length === 0) {
						nextVisible = nextPending.slice(0, VISIBLE_STREAM_COUNT);
						nextPending = nextPending.slice(VISIBLE_STREAM_COUNT);
					}

					return {
						isLoading: false,
						isFetchingMore: false,
						errorText: '',
						streamOffset: prevState.streamOffset + perTypeLimit,
						streamPerTypeLimit: perTypeLimit,
						visibleStreamItems: nextVisible,
						pendingStreamItems: nextPending,
						latestInsertedStreamId: nextVisible.length > 0 ? nextVisible[0]._streamId : prevState.latestInsertedStreamId,
					};
				}, this.ensurePendingBuffer);
			})
			.catch((error) => {
				console.log('public stream load error', error);

				this.setState((prevState) => {
					if (prevState.visibleStreamItems.length > 0 || prevState.pendingStreamItems.length > 0) {
						return {
							isLoading: false,
							isFetchingMore: false,
						};
					}

					return {
						isLoading: false,
						isFetchingMore: false,
						errorText: 'Could not sync the public stream right now.',
						visibleStreamItems: this.decorateStreamItems(FALLBACK_STREAM.slice(0, VISIBLE_STREAM_COUNT)),
						pendingStreamItems: this.decorateStreamItems(FALLBACK_STREAM.slice(VISIBLE_STREAM_COUNT)),
						latestInsertedStreamId: null,
					};
				});
			});
	};

	ensurePendingBuffer = () => {
		const {
			pendingStreamItems,
			isFetchingMore,
		} = this.state;

		if (pendingStreamItems.length <= STREAM_REFILL_THRESHOLD && !isFetchingMore) {
			this.loadPublicStream();
		}
	};

	rotateStream = () => {
		this.setState((prevState) => {
			if (prevState.visibleStreamItems.length === 0 || prevState.pendingStreamItems.length === 0) {
				return null;
			}

			const [nextItem, ...remainingPending] = prevState.pendingStreamItems;

			return {
				visibleStreamItems: [nextItem, ...prevState.visibleStreamItems.slice(0, VISIBLE_STREAM_COUNT - 1)],
				pendingStreamItems: remainingPending,
				latestInsertedStreamId: nextItem._streamId,
			};
		}, this.ensurePendingBuffer);
	};

	getTypeLabel = (type) => {
		switch (type) {
			case 'skill':
				return 'Skill';
			case 'tool':
				return 'Tool';
			case 'soft-skill':
				return 'Soft Skill';
			case 'domain':
				return 'Domain';
			default:
				return 'Signal';
		}
	};

	renderStreamCard = (item) => {
		const {
			latestInsertedStreamId,
		} = this.state;

		const streamCardClassName = item._streamId === latestInsertedStreamId
			? `${styles.streamCard} ${styles.streamCardNew}`
			: styles.streamCard;

		return (
			<article key={item._streamId} className={streamCardClassName}>
				<div className={styles.streamTopRow}>
					<span className={styles.typeChip}>{this.getTypeLabel(item.type)}</span>
					<span className={styles.streamMeta}>just now</span>
				</div>
				<p className={styles.streamText}>
					<span className={styles.streamName}>{item.personName}</span>
					<span className={styles.streamAction}> dropped a verified signal in </span>
					<span className={styles.streamItem}>{item.item}</span>
				</p>
			</article>
		);
	};

	render() {
		const {
			isLoading,
			errorText,
			visibleStreamItems,
		} = this.state;

		return (
			<div className={styles.page}>
				<div className={styles.bgGlowTop} />
				<div className={styles.bgGlowBottom} />
				<div className={styles.bgGrid} />
				<header className={styles.topHeader}>
					<div className={styles.topHeaderInner}>
						<img src={HQLogo} alt="HQVibe" className={styles.topHeaderLogo} />
						<a href="/signin" className={styles.topHeaderBtn}>Sign In</a>
					</div>
				</header>

				<main className={styles.mainFlow}>
					<section
						ref={this.setSectionRef('hero')}
						data-section-key="hero"
						className={`${styles.heroSection} ${this.getRevealClass('hero')}`}
					>
						<span className={styles.logoBadge}>New Age Professional Network</span>
						<h1 className={styles.centerTitle}>
							<span className={styles.centerTitleLine}>Your Career,</span>
							<span className={styles.centerTitleLine}>In Real Time.</span>
						</h1>
						<p className={styles.heroSubtitle}>
							HQVibe turns your proof-of-work into your professional identity.
							Built for a generation that moves fast, ships often, and grows in public.
						</p>
						<div className={styles.actionsRowCenter}>
							<a href="/signin" className={styles.primaryBtn}>Start Building</a>
							<a href="/stream" className={styles.secondaryBtn}>View Network Pulse</a>
						</div>
					</section>

					<section
						ref={this.setSectionRef('stream')}
						data-section-key="stream"
						className={`${styles.shell} ${this.getRevealClass('stream')}`}
					>
						<section className={styles.leftColumn}>
							<h2 className={styles.sectionHeading}>What The Network Sees</h2>
							<p className={styles.subtitle}>
								Static resumes freeze you in the past. HQVibe keeps your profile alive with fresh proof from your actual work.
							</p>

							<div className={styles.bulletList}>
								<div className={styles.bulletItem}>Drop proof of progress in minutes, not months.</div>
								<div className={styles.bulletItem}>Stay discoverable with a constantly evolving skill graph.</div>
								<div className={styles.bulletItem}>Turn small wins into a visible professional trajectory.</div>
							</div>

							<div className={styles.actionsRow}>
								<a href="/signin" className={styles.primaryBtn}>Sign In</a>
							</div>
						</section>

						<section className={styles.rightColumn}>
							<div className={styles.streamHeaderRow}>
								<h2 className={styles.streamTitle}>Public Activity Stream</h2>
								<span className={styles.livePill}>Live</span>
							</div>

							<p className={styles.streamSubtitle}>
								Signals from people leveling up across skills, tools, soft-skills, and domains.
							</p>

							{errorText !== '' && <div className={styles.errorBox}>{errorText}</div>}

							{isLoading && <div className={styles.loadingBox}>Loading stream intelligence...</div>}

							{!isLoading && (
								<div className={styles.streamList}>
									{visibleStreamItems.map(this.renderStreamCard)}
								</div>
							)}
						</section>
					</section>

                    <section
						ref={this.setSectionRef('momentum')}
						data-section-key="momentum"
						className={`${styles.momentumSection} ${this.getRevealClass('momentum')}`}
					>
						<h2 className={styles.sectionHeading}>Built For Momentum</h2>
						<div className={styles.momentumGrid}>
							<article className={styles.momentumCard}>
								<span className={styles.momentumKicker}>Signal 01</span>
								<h3>Live Skill Proof</h3>
								<p>Every update adds compounding proof to your profile, so opportunities find current-you.</p>
							</article>
							<article className={styles.momentumCard}>
								<span className={styles.momentumKicker}>Signal 02</span>
								<h3>Discovery That Hits</h3>
								<p>Get discovered by teams looking for verified ability, not recycled keywords from old resumes.</p>
							</article>
							<article className={styles.momentumCard}>
								<span className={styles.momentumKicker}>Signal 03</span>
								<h3>Professional + Human</h3>
								<p>Show your tools, domain depth, and soft-skills in one identity that evolves with every move.</p>
							</article>
						</div>
					</section>

					<section
						ref={this.setSectionRef('ecosystem')}
						data-section-key="ecosystem"
						className={`${styles.ecosystemSection} ${this.getRevealClass('ecosystem')}`}
					>
						<h2 className={styles.sectionHeading}>One Profile. Multiple Dimensions.</h2>
						<p className={styles.ecosystemIntro}>
							Gen-Z careers are nonlinear by default. You can ship product at noon, freelance at night,
							and mentor a community on weekends. HQVibe lets all of that count as real professional signal.
						</p>
						<div className={styles.trackRow}>
							<div className={styles.trackCard}>
								<span>Skills</span>
								<strong>Execution quality, consistency, and depth over time</strong>
								<em>Show what you can actually do, not just what you claim.</em>
							</div>
							<div className={styles.trackCard}>
								<span>Tools</span>
								<strong>Real stack fluency across product, design, AI, and ops</strong>
								<em>From Figma to Python to Notion, your workflow becomes visible.</em>
							</div>
							<div className={styles.trackCard}>
								<span>Soft Skills</span>
								<strong>Communication, ownership, leadership, and collaboration proof</strong>
								<em>Teams hire people who can ship and work well with humans.</em>
							</div>
							<div className={styles.trackCard}>
								<span>Domains</span>
								<strong>Context that makes your expertise instantly relevant</strong>
								<em>Fintech, creator economy, health, climate, gaming, and beyond.</em>
							</div>
						</div>
					</section>

					<section
						ref={this.setSectionRef('cta')}
						data-section-key="cta"
						className={`${styles.finalSection} ${this.getRevealClass('cta')}`}
					>
						<h2>Build A Reputation That Updates Itself.</h2>
						<p>Join the professional network for the new age. Stay current. Stay visible. Stay undeniable.</p>
						<a href="/signin" className={styles.primaryBtn}>Enter HQVibe</a>
					</section>
				</main>
			</div>
		);
	}
}
